<script>
  /**
   * @event {{ page?: number; pageSize?: number }} change - Dispatched after any user interaction
   * @event {{ page: number; }} click:button--previous
   * @event {{ page: number; }} click:button--next
   * @event {{ pageSize: number; page: number; }} update
   */

  /**
   * Specify if the pagination should use the basic style
   * @type {"default" | "secondary"}
   */
  export let basic = false;

  /** Set to `true` to invert the pagination colors */
  export let dark = false;

  export let small = false;

  /** Specify the current page index */
  export let page = 1;

  /** Specify the total number of items */
  export let totalItems = 0;

  /** Set to `true` to disable the pagination */
  export let disabled = false;

  /** Specify the forward button text */
  export let forwardText = "Next page";

  /** Specify the backward button text */
  export let backwardText = "Previous page";

  /** Specify the items per page text */
  export let itemsPerPageText = "Items per page:";

  /**
   * Override the item text
   * @type {(min: number, max: number) => string}
   */
  export let itemText = (min, max) =>
    `${min}–${max} item${max === 1 ? "" : "s"}`;

  /**
   * Override the item range text
   * @type {(min: number, max: number, total: number) => string}
   */
  export let itemRangeText = (min, max, total) =>
    `${basic ? "<b>" : ""}${min} – ${max}${basic ? "</b>&nbsp" : " "} of ${basic ? "&nbsp<b>" : ""}${total}${basic ? "</b>" : ""}${!basic ? ` item${total === 1 ? "" : "s"}` : ""}`;

  /** Set to `true` to disable the page input */
  export let pageInputDisabled = false;

  /** Set to `true` to disable the page size input */
  export let pageSizeInputDisabled = false;

  /** Specify the number of items to display in a page */
  export let pageSize = 10;

  /**
   * Specify the available page sizes
   * @type {ReadonlyArray<number>}
   */
  export let pageSizes = [10];

  /** Set to `true` if the number of pages is unknown */
  export let pagesUnknown = false;

  /**
   * Override the page text
   * @type {(page: number) => string}
   */
  export let pageText = (page) => `page ${page}`;

  /**
   * Override the page range text
   * @type {(current: number, total: number) => string}
   */
  export let pageRangeText = (current, total) =>
    `of ${total} page${total === 1 ? "" : "s"}`;

  /** Set an id for the top-level element */
  export let id = "ccs-" + Math.random().toString(36);

  import { createEventDispatcher } from "svelte";
  import CaretLeft from "../icons/CaretLeft.svelte";
  import CaretRight from "../icons/CaretRight.svelte";
  import AssistantChevron from "../icons/AssistantChevron.svelte";
  import Button from "../Button/Button.svelte";
  import Select from "../Select/Select.svelte";
  import SelectItem from "../Select/SelectItem.svelte";

  const dispatch = createEventDispatcher();

  $: dispatch("update", { pageSize, page });
  $: totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
  $: if (page > totalPages) page = totalPages;
  $: selectItems = Array.from({ length: totalPages }, (_, i) => i);
  $: backButtonDisabled = disabled || page === 1;
  $: forwardButtonDisabled = disabled || page === totalPages;
</script>

<div
  id="{id}"
  class:bx--pagination="{true}"
  class:bx--pagination--secondary="{basic}"
  class:bx--pagination--dark="{dark}"
  class:bx--pagination--simple="{(pageSizeInputDisabled && pageInputDisabled) || basic}"
  class:bx--pagination--small="{small}"
  {...$$restProps}
>
  <div class:bx--pagination__prev-status="{true}">
    <div class:bx--pagination__status="{true}">
      <div class:bx--pagination__left="{true}">
        {#if !pageSizeInputDisabled && !basic}
          <label
            id="bx--pagination-select-{id}-count-label"
            for="bx--pagination-select-{id}"
            class:bx--pagination__text="{true}"
          >
            {itemsPerPageText}
          </label>
          <Select
            color={dark && !basic ? "dark" : "default"}
            id="bx--pagination-select-{id}"
            class="bx--select__item-count"
            hideLabel
            noLabel
            inline
            disabled="{disabled}"
            on:change="{({ target }) => {
              pageSize = Number(target.value);
              dispatch('change', { pageSize });
            }}"
            bind:selected="{pageSize}"
          >
            {#each pageSizes as size, i (size)}
              <SelectItem value="{size}" text="{size.toString()}" />
            {/each}
          </Select>
        {/if}
        <span
          class:bx--pagination__text="{!pageSizeInputDisabled && !basic}"
          class:bx--pagination__text--left="{!pageSizeInputDisabled && !basic && pageInputDisabled}"
          class:bx--pagination__text--right="{pageSizeInputDisabled && !pageInputDisabled && !basic}"
        >
          {#if pagesUnknown && !basic}
            {itemText(pageSize * (page - 1) + 1, page * pageSize)}
          {:else}
            {@html itemRangeText(
              Math.min(pageSize * (page - 1) + 1, totalItems),
              Math.min(page * pageSize, totalItems),
              totalItems
            )}
          {/if}
        </span>
      </div>
      <div class:bx--pagination__right="{true}">
        {#if !pageInputDisabled && !basic}
          <Select
          color={dark && !basic ? "dark" : "default"}
            id="bx--pagination-select-{id + 2}"
            class="bx--select__page-number"
            labelText="Page number, of {totalPages} pages"
            inline
            disabled="{disabled}"
            hideLabel
            on:change="{({ target }) => {
              page = Number(target.value);
              dispatch('change', { page });
            }}"
            bind:selected="{page}"
          >
            {#each selectItems as size, i (size)}
              <SelectItem value="{size + 1}" text="{(size + 1).toString()}" />
            {/each}
          </Select>
          <span class:bx--pagination__text="{true}">
            {#if pagesUnknown}
              {pageText(page)}
            {:else}{pageRangeText(page, totalPages)}{/if}
          </span>
        {/if}
      </div>
    </div>
    <Button
      kind={basic || dark ? "secondary" : "ghost"}
      tooltipAlignment={!basic ? "center" : "start"}
      tooltipPosition={ !basic ? "top" : "right"}
      icon={!basic ? CaretLeft: AssistantChevron}
      iconDescription="{!basic ? backwardText : ""}"
      disabled="{backButtonDisabled}"
      size={basic && small ? "field" : "default"}
      class="bx--pagination__button bx--pagination__button--backward {backButtonDisabled
        ? 'bx--pagination__button--no-index'
        : ''}"
      on:click="{() => {
        page--;
        dispatch('click:button--previous', { page });
        dispatch('change', { page });
      }}"
    />
  </div>
  <Button
    kind={basic || dark ? "secondary" : "ghost"}
    tooltipAlignment="center"
    tooltipPosition={ !basic ? "top" : "left"}
    icon={!basic ? CaretRight: AssistantChevron}
    iconDescription="{!basic ? forwardText : ""}"
    disabled="{forwardButtonDisabled}"
    size={basic && small ? "field" : "default"}
    class="bx--pagination__button bx--pagination__button--forward {forwardButtonDisabled
      ? 'bx--pagination__button--no-index'
      : ''}"
    on:click="{() => {
      page++;
      dispatch('click:button--next', { page });
      dispatch('change', { page });
    }}"
  />
</div>